import { ChangeDetectorRef, Component, effect, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { OSAffectedControlType, TradeInImeiErrorType } from '@app/sales/modules/mobilephone/models/trade-in-enums';
import { StorageCapacityAlternative } from '@app/sales/modules/mobilephone/models/trade-in-api.model';
import { SetSelectedStorageCapacityAction } from '@app/sales/states/customer-product-trade-in/customer-product-trade-in.actions';
import { TradeInSelectors } from '@app/sales/states/customer-product-trade-in/customer-product-trade-in.selectors';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { IceInputFieldComponent } from '@app/shared/components/molecules/ice-input-field/ice-input-field.component';
import { select, Store } from '@ngxs/store';
import { SortByPipe } from '@sales/modules/mbb/pipes/sort-by.pipe';
import { Subscription } from 'rxjs';
import { InformationButtonComponent } from '../../../information-button/information-button.component';

@Component({
  selector: 'app-trade-in-specifications',
  templateUrl: './trade-in-specifications.component.html',
  styleUrl: './trade-in-specifications.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IceInputFieldComponent,
    IceAlertComponent,
    SpinnerComponent,
    MatFormField,
    MatSelect,
    MatOption,
    SortByPipe,
    InformationButtonComponent,
  ],
})
export class TradeInSpecificationsComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);

  OSAffectedControlType = OSAffectedControlType;

  selectedStep = select(TradeInSelectors.selectedStep);
  isValidatingImei = select(TradeInSelectors.isValidatingImei);
  validateTradeInImeiErrorState = select(TradeInSelectors.validateTradeInImeiError);
  storageCapacityAlternatives = select(TradeInSelectors.storageCapacityAlternatives);
  hasAcceptedTradeInVoucher = select(TradeInSelectors.hasAcceptedTradeInVoucher);

  @Input() tradeInSpecificationsForm: FormGroup;

  @Output() infoClicked = new EventEmitter<OSAffectedControlType | undefined>();

  activeInformation: OSAffectedControlType;

  protected readonly compareStorageCapacity = (
    a: StorageCapacityAlternative | null | undefined,
    b: StorageCapacityAlternative | null | undefined
  ): boolean => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return a.size === b.size && a.unit === b.unit;
  };

  get validateTradeInImeiError(): string {
    switch (this.validateTradeInImeiErrorState()) {
      case TradeInImeiErrorType.ImeiAlreadyInUseByActiveMobilBytteAgreement:
        return 'Det er ikke mulig å registrere MobilPant for dette IMEI-nummeret. Telefonen har en MobilBytte-avtale med pågående bytte-prosess.';
      case TradeInImeiErrorType.ImeiIsAlreadyInUseOnAnActiveTradeInProcess:
        return 'Det finnes allerede en aktiv MobilPant-prosess for telefonen med dette IMEI-nummeret.';
      case TradeInImeiErrorType.InvalidImei:
        return 'MobilPant er ikke tilgjengelig for dette IMEI-nummeret. Sjekk at riktig IMEI-nummer er registrert.';
      case TradeInImeiErrorType.ProductIsNotAvailableForTradeIn:
        return 'MobilPant er ikke tilgjengelig for denne telefonmodellen. De aller nyeste telefonmodellene kan ikke pantes.';
      default:
        return '';
    }
  }

  get storageControl(): FormControl {
    const storageControl = this.tradeInSpecificationsForm.controls.storage as FormControl;

    if (!this.storageCapacityAlternatives()?.length) {
      storageControl.disable();
    } else if (storageControl.disabled && !this.hasAcceptedTradeInVoucher()) {
      storageControl.enable();
    }

    return storageControl;
  }

  subscriptions$: Subscription[] = [];

  constructor() {
    effect(() => {
      // Reset storage control value if imei validation fails, and show error
      if (!!this.validateTradeInImeiErrorState()) {
        this.storageControl.setValue('');
        this.tradeInSpecificationsForm.controls.imei.markAsTouched();
      }
    });
    effect(() => {
      if (!Number.isNaN(this.selectedStep())) {
        this.activeInformation = undefined;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    this.subscriptions$.push(
      // Set storage control value in store
      this.storageControl.valueChanges.subscribe(value => {
        this.store.dispatch(new SetSelectedStorageCapacityAction(value));
      })
    );
  }

  toggleActiveInformation(information: OSAffectedControlType): void {
    this.activeInformation = information === this.activeInformation ? null : information;
    this.infoClicked.emit(this.activeInformation);
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(subscription => subscription.unsubscribe());
  }
}
